import { calcDocument, D, dec } from '@makila/fiscal';
/** O estado da ordem só anda para a frente; entregue (cobrada) ganha a anulada. */
export const WORK_RANK = { received: 0, in_progress: 1, ready: 2, cancelled: 3, delivered: 4 };
function mergeWork(a, b, head) {
    const status = WORK_RANK[a.status] >= WORK_RANK[b.status] ? a.status : b.status;
    const seen = new Set();
    const history = [...a.history, ...b.history].filter((h) => { const k = `${h.status}|${h.at}`; if (seen.has(k))
        return false; seen.add(k); return true; })
        .sort((x, y) => x.at.localeCompare(y.at) || WORK_RANK[x.status] - WORK_RANK[y.status]);
    // O orçamento mais recente; a resposta do cliente a esse orçamento não se perde.
    const qa = a.quote, qb = b.quote;
    let quote = qa && qb ? (qa.at === qb.at ? { ...qa, ...qb, approvedAt: qa.approvedAt ?? qb.approvedAt ?? null, refusedAt: qa.refusedAt ?? qb.refusedAt ?? null, by: qa.by ?? qb.by ?? null } : qa.at > qb.at ? qa : qb) : qa ?? qb;
    if (quote && quote.approvedAt)
        quote = { ...quote, refusedAt: null };
    return { ...head, status, history, ...(quote ? { quote } : {}) };
}
/** A versão mais recente; num empate, uma escolha fixa (a mesma em qualquer caixa e em qualquer ordem). */
function later(a, b) {
    if (a.updatedAt !== b.updatedAt)
        return a.updatedAt > b.updatedAt ? a : b;
    return JSON.stringify(a) >= JSON.stringify(b) ? a : b;
}
function mergeItem(a, b) {
    // Pago é definitivo (o documento já saiu): fica a versão paga.
    if (a.paidIn && !b.paidIn)
        return a;
    if (b.paidIn && !a.paidIn)
        return b;
    const win = later(a, b), lose = win === a ? b : a;
    // O estado de preparação só anda para a frente (a cozinha marcou pronto; a sala, entretanto, mudou a nota).
    const rank = { new: 0, sent: 1, ready: 2, served: 3, cancelled: -1 };
    if (win.status !== 'cancelled' && lose.status !== 'cancelled' && rank[lose.status] > rank[win.status])
        return { ...win, status: lose.status };
    return win;
}
export function mergeOrder(a, b) {
    if (a.id !== b.id)
        throw new Error('Comandas diferentes');
    const head = later(a, b);
    const items = new Map();
    for (const it of [...a.items, ...b.items]) {
        const cur = items.get(it.id);
        items.set(it.id, cur ? mergeItem(cur, it) : it);
    }
    // Fila: o estado só anda para a frente (à espera → em atendimento → concluído/saiu), seja qual for a ordem dos retratos.
    const qrank = { waiting: 0, serving: 1, done: 2, left: 2 };
    const queue = a.queue && b.queue ? (qrank[a.queue.status] === qrank[b.queue.status] ? head.queue : qrank[a.queue.status] > qrank[b.queue.status] ? a.queue : b.queue) : head.queue ?? a.queue ?? b.queue;
    // Fechada (conta emitida) ganha a cancelada, que ganha a aberta.
    const ended = [a, b].find((o) => o.status === 'closed') ?? [a, b].find((o) => o.status === 'cancelled');
    return {
        ...head,
        status: ended ? ended.status : 'open',
        closedAt: a.closedAt ?? b.closedAt,
        mergedInto: a.mergedInto ?? b.mergedInto,
        ...((a.appointmentId ?? b.appointmentId) ? { appointmentId: a.appointmentId ?? b.appointmentId } : {}),
        ...(queue ? { queue } : {}),
        ...(a.work && b.work ? { work: mergeWork(a.work, b.work, head.work ?? a.work) } : (a.work ?? b.work) ? { work: (a.work ?? b.work) } : {}),
        items: [...items.values()].sort((x, y) => x.addedAt.localeCompare(y.addedAt) || x.id.localeCompare(y.id)),
        documents: [...new Set([...a.documents, ...b.documents])],
    };
}
/** Preço unitário do item com os modificadores ("+ queijo 50"). */
export const itemUnitPrice = (i) => i.modifiers.reduce((s, m) => s.plus(m.price), dec(i.unitPrice)).toString();
/** Itens que contam para a conta (não cancelados, ainda não pagos). */
export const billableItems = (o) => o.items.filter((i) => i.status !== 'cancelled' && !i.paidIn);
/** Linhas do documento para os itens escolhidos (todos, por omissão). */
export function orderLines(o, itemIds) {
    const pick = billableItems(o).filter((i) => !itemIds || itemIds.includes(i.id));
    return pick.map((i) => ({
        productId: i.productId,
        description: i.modifiers.length ? `${i.description} (${i.modifiers.map((m) => m.name).join(', ')})` : i.description,
        quantity: i.quantity, unitPrice: itemUnitPrice(i), taxRate: i.taxRate, priceIncludesTax: i.priceIncludesTax, exemptionCode: i.exemptionCode ?? null,
        ...(i.staffId ? { staffId: i.staffId } : {}),
        ...(i.serial ? { serial: i.serial, ...(i.warrantyUntil ? { warrantyUntil: i.warrantyUntil } : {}) } : {}),
    }));
}
/**
 * Total do que falta pagar na comanda, calculado como a venda o vai calcular
 * (modificadores, IVA de preços sem IVA, arredondamento por linha). Os ecrãs
 * mostram este — nunca preço × quantidade (AUD-21).
 */
export function orderTotal(o) {
    const lines = orderLines(o);
    return lines.length ? calcDocument({ lines }).grossTotal : '0.00';
}
/** Partes iguais de um total, ao centavo (a última leva a sobra): uma conta, vários pagamentos. */
export function splitEqual(total, parts) {
    if (!Number.isInteger(parts) || parts < 2 || parts > 50)
        throw new Error('Divida entre 2 e 50 partes');
    const each = new D(total).div(parts).toDecimalPlaces(2, D.ROUND_DOWN);
    const out = Array.from({ length: parts }, () => each);
    out[parts - 1] = new D(total).minus(each.times(parts - 1));
    return out.map((x) => x.toFixed(2));
}
/** Itens novos por estação, para o talão da cozinha ("enviar"). */
export function kitchenTicket(o) {
    const fresh = o.items.filter((i) => i.status === 'new');
    const by = new Map();
    for (const i of fresh)
        by.set(i.station ?? 'Cozinha', [...(by.get(i.station ?? 'Cozinha') ?? []), i]);
    return [...by].map(([station, items]) => ({ station, items }));
}
// ── Operações (puras: devolvem a comanda nova, que se grava com saveOrder) ──
const iso = (d) => d.toISOString();
export function newOrder(p) {
    if (p.kind === 'delivery' && (!p.delivery?.name.trim() || !p.delivery.address.trim()))
        throw new Error('A entrega precisa do nome e da morada');
    return {
        ...(p.delivery ? { delivery: { name: p.delivery.name.trim(), phone: p.delivery.phone?.trim() || null, address: p.delivery.address.trim() } } : {}),
        id: p.id, kind: p.kind, tableId: p.tableId ?? null, label: p.label, guests: p.guests ?? null, status: 'open',
        openedBy: p.operatorId, openedAt: iso(p.now), updatedAt: iso(p.now), items: [], documents: [], terminalId: p.terminalId,
    };
}
export function addItem(o, it, now) {
    if (dec(it.quantity).lessThanOrEqualTo(0))
        throw new Error('Quantidade inválida');
    const item = { ...it, modifiers: it.modifiers ?? [], note: it.note?.trim() || null, status: 'new', addedAt: iso(now), updatedAt: iso(now) };
    return { ...o, items: [...o.items, item], updatedAt: iso(now) };
}
const touch = (o, id, f, now) => {
    if (!o.items.some((i) => i.id === id))
        throw new Error('Item não existe nesta comanda');
    return { ...o, items: o.items.map((i) => (i.id === id ? { ...f(i), updatedAt: iso(now) } : i)), updatedAt: iso(now) };
};
/** Quantidade de um item ainda não enviado (o enviado só se cancela, com motivo). */
export function setItemQuantity(o, id, quantity, now) {
    return touch(o, id, (i) => {
        if (i.status !== 'new')
            throw new Error('Este item já foi para a cozinha: cancele-o (com motivo) em vez de o alterar');
        if (dec(quantity).lessThanOrEqualTo(0))
            return { ...i, status: 'cancelled', cancelReason: 'Retirado antes de enviar' };
        return { ...i, quantity: dec(quantity).toString() };
    }, now);
}
/** "Enviar": os itens novos passam a enviados (e saem no talão da cozinha). */
export function sendToKitchen(o, now) {
    const at = iso(now);
    return { ...o, items: o.items.map((i) => (i.status === 'new' ? { ...i, status: 'sent', updatedAt: at } : i)), updatedAt: at };
}
/** Cozinha/bar: pronto; sala: servido. Só para a frente. */
export function setItemStatus(o, id, status, now) {
    const rank = { new: 0, sent: 1, ready: 2, served: 3, cancelled: 9 };
    return touch(o, id, (i) => {
        if (i.status === 'cancelled' || i.paidIn)
            throw new Error('Item cancelado ou já pago');
        if (rank[status] <= rank[i.status])
            return i;
        return { ...i, status };
    }, now);
}
/** Anular um item: os já enviados exigem o motivo e quem autorizou (gerente). */
export function cancelItem(o, id, p, now) {
    return touch(o, id, (i) => {
        if (i.paidIn)
            throw new Error('Item já pago: corrige-se com nota de crédito');
        if (i.status !== 'new' && !p.reason?.trim())
            throw new Error('Indique o motivo da anulação');
        return { ...i, status: 'cancelled', cancelReason: p.reason?.trim() || 'Retirado antes de enviar', cancelledBy: p.by };
    }, now);
}
export function moveOrder(o, tableId, label, now) {
    return { ...o, tableId, label, updatedAt: iso(now) };
}
/** Juntar mesas: os itens por pagar da `source` passam para a `target`; a source fica fechada como juntada. */
export function mergeInto(target, source, now) {
    if (target.id === source.id)
        throw new Error('A mesma comanda');
    const at = iso(now);
    const moving = billableItems(source).map((i) => ({ ...i, updatedAt: at }));
    return {
        target: { ...target, items: [...target.items, ...moving], updatedAt: at },
        source: { ...source, status: 'cancelled', mergedInto: target.id, items: source.items.map((i) => (moving.some((m) => m.id === i.id) ? { ...i, status: 'cancelled', cancelReason: `Passou para ${target.label}`, updatedAt: at } : i)), updatedAt: at },
    };
}
/** Passar itens de uma comanda para outra (o amigo que muda de conta): saem de uma e entram na outra, com o mesmo id. */
export function moveItems(source, target, itemIds, now) {
    if (source.id === target.id)
        throw new Error('A mesma comanda');
    if (target.status !== 'open')
        throw new Error(`${target.label} já não está aberta`);
    const at = iso(now);
    const moving = billableItems(source).filter((i) => itemIds.includes(i.id));
    if (!moving.length || moving.length !== new Set(itemIds).size)
        throw new Error('Escolha itens por pagar desta conta');
    return {
        target: { ...target, items: [...target.items, ...moving.map((i) => ({ ...i, updatedAt: at }))], updatedAt: at },
        source: { ...source, items: source.items.map((i) => (itemIds.includes(i.id) ? { ...i, status: 'cancelled', cancelReason: `Passou para ${target.label}`, updatedAt: at } : i)), updatedAt: at },
    };
}
/** Comanda sem nada (aberta por engano): fecha-se cancelada. */
export function cancelOrder(o, now) {
    if (billableItems(o).some((i) => i.status !== 'new'))
        throw new Error('Há itens já enviados: anule-os primeiro (com motivo)');
    return { ...o, status: 'cancelled', items: o.items.map((i) => (i.status === 'cancelled' || i.paidIn ? i : { ...i, status: 'cancelled', cancelReason: 'Comanda anulada', updatedAt: iso(now) })), updatedAt: iso(now) };
}
export function checkModifierGroup(g) {
    if (!g.name.trim())
        throw new Error('Indique o nome do grupo');
    if (!Number.isInteger(g.min) || !Number.isInteger(g.max) || g.min < 0 || g.max < 1 || g.max < g.min)
        throw new Error('Mínimo e máximo inválidos');
    if (!g.options.length || g.options.length > 50)
        throw new Error('Indique entre 1 e 50 opções');
    if (g.min > g.options.length)
        throw new Error('O mínimo passa do número de opções');
    for (const o of g.options) {
        if (!o.name.trim())
            throw new Error('Opção sem nome');
        if (dec(o.price).isNegative() || dec(o.price).decimalPlaces() > 2)
            throw new Error(`Preço inválido em ${o.name}`);
    }
    if (new Set(g.options.map((o) => o.name.trim().toLowerCase())).size !== g.options.length)
        throw new Error('Opções repetidas');
    if (!g.productIds?.length && !g.categoryIds?.length)
        throw new Error('Escolha os artigos ou as categorias');
}
/** Grupos que se pedem ao juntar este artigo. */
export const groupsFor = (groups, productId, categoryId) => groups.filter((g) => g.active && ((g.productIds?.includes(productId) ?? false) || (!!categoryId && (g.categoryIds?.includes(categoryId) ?? false))));
/** Confere a escolha (por grupo) e devolve os modificadores do item, pela ordem dos grupos. */
export function pickModifiers(groups, chosen) {
    const out = [];
    for (const g of groups) {
        const names = chosen[g.id] ?? [];
        if (names.length < g.min)
            throw new Error(`${g.name}: escolha ${g.min === 1 ? 'uma opção' : `pelo menos ${g.min}`}`);
        if (names.length > g.max)
            throw new Error(`${g.name}: no máximo ${g.max}`);
        for (const n of names) {
            const o = g.options.find((x) => x.name === n);
            if (!o)
                throw new Error(`${g.name}: opção desconhecida (${n})`);
            out.push({ name: o.name, price: dec(o.price).toString() });
        }
    }
    return out;
}
// ── Fila de espera (beleza, lavagem auto) ────────────────────────────────
/** Senha nova: um retrato de comanda do tipo fila, à espera, com os serviços pedidos. */
export function newQueueTicket(p) {
    if (!p.customer.name.trim())
        throw new Error('Indique o nome do cliente');
    const o = newOrder({ id: p.id, kind: 'queue', label: `${p.ticket} · ${p.customer.name.trim()}`, operatorId: p.operatorId, terminalId: p.terminalId, now: p.now });
    const withQueue = { ...o, queue: { ticket: p.ticket, customer: { ...p.customer, name: p.customer.name.trim() }, preferredStaffId: p.preferredStaffId, status: 'waiting', ...(p.plate ? { plate: p.plate.trim().toUpperCase() } : {}) } };
    return p.services.reduce((acc, sv) => addItem(acc, {
        id: sv.itemId, productId: sv.productId, description: sv.description, unitPrice: sv.unitPrice, taxRate: sv.taxRate, priceIncludesTax: sv.priceIncludesTax,
        exemptionCode: sv.exemptionCode ?? null, quantity: '1', station: null, addedBy: p.operatorId, staffId: p.preferredStaffId, durationMin: sv.durationMin ?? null,
    }, p.now), withQueue);
}
/** "Atender": o profissional chama o cliente; os serviços ainda sem profissional ficam com ele. */
export function startService(o, staffId, now) {
    if (!o.queue || o.status !== 'open')
        throw new Error('Esta senha já não está na fila');
    if (o.queue.status !== 'waiting')
        throw new Error(o.queue.status === 'serving' ? 'Este cliente já está a ser atendido' : 'Esta senha já foi concluída');
    const at = iso(now);
    return {
        ...o, updatedAt: at, queue: { ...o.queue, status: 'serving', servingBy: staffId, startedAt: at },
        items: o.items.map((i) => (i.staffId || i.status === 'cancelled' ? i : { ...i, staffId, updatedAt: at })),
    };
}
/** O cliente foi embora sem ser atendido (não há documento: a senha fecha cancelada). */
export function leaveQueue(o, by, now) {
    if (!o.queue || o.queue.status === 'done')
        throw new Error('Esta senha já foi concluída');
    const at = iso(now);
    return { ...o, status: 'cancelled', updatedAt: at, closedAt: at, queue: { ...o.queue, status: 'left' }, items: o.items.map((i) => (i.paidIn ? i : { ...i, status: 'cancelled', cancelReason: 'Saiu da fila', cancelledBy: by, updatedAt: at })) };
}
/** Duração prevista de uma senha (soma dos serviços; 20 min se nada se sabe). */
export const ticketMinutes = (o) => billableItems(o).reduce((s, i) => s + (i.durationMin ?? 0) * Number(i.quantity), 0) || 20;
/**
 * Espera prevista (min) para cada senha à espera, por profissional: o que
 * falta a quem está a ser atendido mais as senhas à frente na coluna dele
 * (ou na de "o primeiro livre", que vai para quem acabar primeiro).
 */
export function queueEstimates(open, staffIds, now) {
    const free = new Map(staffIds.map((id) => [id, 0]));
    for (const o of open) {
        if (o.queue?.status !== 'serving' || !o.queue.servingBy || !free.has(o.queue.servingBy))
            continue;
        const spent = (now.getTime() - Date.parse(o.queue.startedAt ?? o.openedAt)) / 60_000;
        free.set(o.queue.servingBy, Math.max(free.get(o.queue.servingBy), Math.max(0, ticketMinutes(o) - spent)));
    }
    const out = new Map();
    const waiting = open.filter((o) => o.queue?.status === 'waiting').sort((a, b) => a.queue.ticket - b.queue.ticket || a.openedAt.localeCompare(b.openedAt));
    for (const o of waiting) {
        const pref = o.queue.preferredStaffId;
        const target = pref && free.has(pref) ? pref : [...free.entries()].sort((x, y) => x[1] - y[1])[0]?.[0];
        if (!target) {
            out.set(o.id, 0);
            continue;
        }
        const at = free.get(target);
        out.set(o.id, Math.round(at));
        free.set(target, at + ticketMinutes(o));
    }
    return out;
}
// ── Contas por nome (bar, discoteca) ───────────────────────────────────────
const norm = (s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
/** Conta nova: o nome (e o cartão, se houver) não se repete entre as contas abertas da loja. */
export function newTab(p) {
    const name = p.name.trim().replace(/\s+/g, ' ');
    if (!name)
        throw new Error('Indique o nome da conta');
    if (name.length > 60)
        throw new Error('Nome demasiado comprido');
    const card = p.card?.trim() || null;
    const limit = p.limit?.trim() ? dec(p.limit) : null;
    if (limit && (limit.lessThanOrEqualTo(0) || limit.decimalPlaces() > 2))
        throw new Error('Limite inválido');
    const tabs = p.open.filter((o) => o.status === 'open' && o.kind === 'tab');
    if (tabs.some((o) => norm(o.tab?.name ?? o.label) === norm(name)))
        throw new Error(`Já há uma conta aberta com o nome ${name}`);
    if (card && tabs.some((o) => o.tab?.card === card))
        throw new Error(`O cartão ${card} já está numa conta aberta`);
    const o = newOrder({ id: p.id, kind: 'tab', label: name, operatorId: p.operatorId, terminalId: p.terminalId, now: p.now });
    return { ...o, tab: { name, card, limit: limit ? limit.toString() : null } };
}
/** Total por pagar e situação face ao limite (alerta a partir de 80%). */
export function tabState(o) {
    const lines = orderLines(o);
    const total = lines.length ? calcDocument({ lines }).grossTotal : '0.00';
    const limit = o.tab?.limit ?? null;
    if (!limit)
        return { total, limit, level: 'ok' };
    const t = new D(total);
    return { total, limit, level: t.greaterThan(limit) ? 'over' : t.greaterThanOrEqualTo(new D(limit).times(0.8)) ? 'near' : 'ok' };
}
/** Procura a conta pelo cartão (exacto) ou pelo nome (parte). */
export function findTabs(open, q) {
    const s = norm(q);
    const tabs = open.filter((o) => o.status === 'open' && o.kind === 'tab');
    if (!s)
        return tabs;
    const byCard = tabs.filter((o) => o.tab?.card && norm(o.tab.card) === s);
    return byCard.length ? byCard : tabs.filter((o) => norm(o.tab?.name ?? o.label).includes(s));
}
// ── Doses (bar) ─────────────────────────────────────────────────────────────
/**
 * Reparte `ml` vendidos pela garrafa aberta e pelas seguintes. Devolve as
 * garrafas por onde passou, por ordem: a primeira é a aberta (se havia,
 * `isNew` = false); cada `full` fechou cheia de vendas e a última fica
 * aberta. Cada garrafa nova sai do stock.
 */
export function pourDoses(cur, capacityMl, ml) {
    const cap = dec(capacityMl);
    let left = dec(ml);
    if (cap.lessThanOrEqualTo(0))
        throw new Error('Garrafa sem capacidade');
    if (left.lessThanOrEqualTo(0))
        return [];
    const out = [];
    let sold = cur ? dec(cur.soldMl) : new D(0);
    let isNew = !cur;
    for (;;) {
        const room = D.max(cap.minus(sold), 0);
        if (left.lessThanOrEqualTo(room)) {
            out.push({ isNew, soldMl: sold.plus(left).toString(), full: false });
            return out;
        }
        out.push({ isNew, soldMl: D.max(sold, cap).toString(), full: true });
        left = left.minus(room);
        sold = new D(0);
        isNew = true;
        if (out.length > 10_000)
            throw new Error('Doses a mais para esta garrafa');
    }
}
//# sourceMappingURL=orders.js.map